import { useEffect } from 'react';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { muiThemeConfig } from '../theme/muiThemeConfig'; // Fase A: ambiente MUI PINADO (mesmo do design-package)

/**
 * Provider que aplica o tema customizado ao Material-UI
 * Converte as configurações do tema JSON em um tema do Material-UI
 */
export default function ThemeApplier({ children, baseTheme }) {
  const { currentTheme, getColor } = useThemeCustom();

  // Cria tema Material-UI baseado nas configurações do tema customizado
  const muiTheme = createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      mode: 'light', // Força modo light independente da preferência do sistema
      primary: {
        main: getColor('primary'),
        dark: getColor('primaryDark'),
        light: getColor('primaryLight'),
      },
      secondary: {
        main: getColor('secondary'),
        dark: getColor('secondaryDark'),
        light: getColor('secondaryLight'),
      },
      background: {
        default: '#FFFFFF',
        paper: getColor('backgroundPaper'),
      },
      text: {
        primary: getColor('text'),
        secondary: getColor('textSecondary'),
        disabled: getColor('textDisabled'),
      },
      success: {
        main: getColor('success'),
      },
      error: {
        main: getColor('error'),
      },
      warning: {
        main: getColor('warning'),
      },
      info: {
        main: getColor('info'),
      },
      divider: getColor('divider'),
    },
    typography: {
      ...baseTheme.typography,
      fontFamily: currentTheme.fonts.fontFamily,
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: currentTheme.customStyles?.borderRadius || '8px',
            boxShadow: currentTheme.customStyles?.boxShadowCard || '0 2px 12px rgba(0, 0, 0, 0.06)',
            transition: currentTheme.customStyles?.transition || 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: currentTheme.customStyles?.boxShadowHover || '0 4px 16px rgba(0, 0, 0, 0.12)',
              transform: currentTheme.customStyles?.hoverTransform || 'translateY(-2px)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: currentTheme.customStyles?.borderRadius || '8px',
          },
          elevation1: {
            boxShadow: currentTheme.customStyles?.boxShadow || '0 2px 8px rgba(0, 0, 0, 0.08)',
          },
          elevation2: {
            boxShadow: currentTheme.customStyles?.boxShadowCard || '0 2px 12px rgba(0, 0, 0, 0.06)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: currentTheme.customStyles?.borderRadiusSmall || '6px',
            textTransform: 'none',
            fontWeight: 500,
            transition: currentTheme.customStyles?.transition || 'all 0.2s ease-in-out',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: currentTheme.customStyles?.boxShadow || '0 2px 8px rgba(0, 0, 0, 0.08)',
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            boxShadow: currentTheme.customStyles?.boxShadow || '0 2px 8px rgba(0, 0, 0, 0.08)',
            '&:hover': {
              boxShadow: currentTheme.customStyles?.boxShadowHover || '0 4px 16px rgba(0, 0, 0, 0.12)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: currentTheme.customStyles?.borderRadiusSmall || '6px',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: currentTheme.customStyles?.borderRadiusLarge || '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: currentTheme.customStyles?.borderRadiusSmall || '6px',
              transition: currentTheme.customStyles?.transition || 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
              },
            },
          },
        },
      },
    },
  }, muiThemeConfig); // Fase A: deep-merge do ambiente pinado (typography line-heights, MuiSwitch chunky, densidade)

  // Aplica CSS variables globais
  useEffect(() => {
    const root = document.documentElement;

    // Aplica cores
    Object.entries(currentTheme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--theme-color-${key}`, value);
    });

    // Aplica fontes
    Object.entries(currentTheme.fonts).forEach(([key, value]) => {
      root.style.setProperty(`--theme-font-${key}`, value);
    });

    // Aplica estilos customizados se existirem
    if (currentTheme.customStyles) {
      Object.entries(currentTheme.customStyles).forEach(([key, value]) => {
        root.style.setProperty(`--theme-custom-${key}`, value);
      });
    }

    // ── Fase B (coexistência): emite TAMBÉM as CSS vars CANÔNICAS (--color-*, --text-*, --radius-*,
    // --font-*) que as Views novas (do designer) consomem via var(--*). Aditivo: as --theme-* acima
    // continuam intactas p/ o legado. Mapeia o tema do tenant atual → nomes canônicos. Ver docs/DESIGN-FLOW.md secao 5.
    const c = currentTheme.colors || {};
    const f = currentTheme.fonts || {};
    const cs = currentTheme.customStyles || {};
    const canonical = {
      '--color-primary': c.primary,
      '--color-primary-dark': c.primaryDark,
      '--color-primary-light': c.primaryLight,
      // vars novas do designer sem equivalente no schema legado → derivação/fallback:
      '--color-primary-soft': c.primarySoft || cs.primarySoft || (c.primary ? `${c.primary}1F` : undefined),
      '--on-primary': c.onPrimary || c.textOnDark || '#ffffff',
      '--color-secondary': c.secondary,
      '--color-success': c.success,
      '--color-warning': c.warning,
      '--color-error': c.error,
      '--color-info': c.info,
      '--text-primary': c.text,
      '--text-secondary': c.textSecondary,
      '--divider': c.divider,
      '--bg-page': c.background,
      '--bg-drawer': c.backgroundDrawer || c.background,
      // Texto do menu sobre o drawer. Fallback = text global (drawer claro); temas de drawer
      // ESCURO (tattoo-studio, personal-trainer, teacher) definem textDrawer claro no JSON.
      '--text-drawer': c.textDrawer || c.text,
      '--drawer-accent': c.drawerAccent || c.primary,
      '--surface': c.backgroundPaper,
      '--radius-card': cs.borderRadius,
      '--radius-sm': cs.borderRadiusSmall,
      '--radius-lg': cs.borderRadiusLarge,
      '--shadow-card': cs.boxShadowCard,
      '--shadow-hover': cs.boxShadowHover,
      '--font-family': f.fontFamily,
      '--font-heading': f.fontFamilyHeadings || f.fontFamily,
    };
    Object.entries(canonical).forEach(([k, v]) => {
      if (v != null) root.style.setProperty(k, v);
    });
  }, [currentTheme]);

  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
}

ThemeApplier.propTypes = {
  children: PropTypes.node.isRequired,
  baseTheme: PropTypes.object.isRequired,
};
